"use client";

import Link from "next/link";
import { AlertTriangle } from "lucide-react";
import BrandLogo from "@/components/tenant/BrandLogo";

/**
 * Tampilan bersama untuk semua error boundary.
 *
 * Dua hal yang sengaja TIDAK dilakukan di sini:
 * 1. Tidak menampilkan `error.message`. Pesan itu berasal dari exception runtime
 *    (sering minified/teknis) dan tidak memberi pengguna satu pun langkah lanjutan.
 *    Yang ditampilkan adalah `digest` — kode pendek yang bisa disebut ke admin
 *    dan dicocokkan dengan log server.
 * 2. Tidak memakai `reset()`. Sejak Next 16.2 ada `unstable_retry()` yang
 *    benar-benar mengambil ulang data lalu merender ulang; `reset()` hanya
 *    membersihkan state boundary sehingga render yang sama gagal lagi.
 */
export default function ErrorScreen({
  title,
  description,
  digest,
  onRetry,
  homeHref = "/dashboard",
  homeLabel = "Ke Dashboard",
}: {
  title: string;
  description: string;
  digest?: string;
  onRetry: () => void;
  homeHref?: string;
  homeLabel?: string;
}) {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-md rounded-2xl border border-[rgba(0,0,0,0.07)] bg-white p-8 text-center shadow-sm">
        <div className="mb-6 flex justify-center">
          <BrandLogo variant="header" />
        </div>

        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
          <AlertTriangle className="h-6 w-6 text-red-600" aria-hidden="true" />
        </div>

        <h1 className="mb-2 text-lg font-bold text-warm-black">{title}</h1>
        <p className="mb-6 text-sm leading-relaxed text-[#6B7280]">{description}</p>

        <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={onRetry}
            className="min-h-11 rounded-xl bg-[var(--brand-primary)] px-5 text-sm font-semibold text-[var(--brand-on-primary,#fff)] transition-colors hover:bg-[var(--brand-primary-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)] focus-visible:ring-offset-2"
          >
            Coba Lagi
          </button>
          <Link
            href={homeHref}
            className="inline-flex min-h-11 items-center justify-center rounded-xl border border-black/10 px-5 text-sm font-medium text-[#374151] transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)] focus-visible:ring-offset-2"
          >
            {homeLabel}
          </Link>
        </div>

        {digest && (
          <p className="mt-5 border-t border-black/[0.06] pt-4 text-[11px] text-[#9CA3AF]">
            Kode error: <code className="font-mono">{digest}</code>
            <br />
            Sebutkan kode ini kalau kamu menghubungi admin.
          </p>
        )}
      </div>
    </div>
  );
}
